<template>
  <div class="support-wrapper px-3">
    <v-container class="custom-container col-height">

      <!-- 🔍 Top Bar -->
      <v-row dense align="center" class="mb-3">
        <v-col sm="4" md="4">
          <v-skeleton-loader v-if="isInitialLoading" type="text" class="mt-5" :loading="true" width="300" />
          <v-skeleton-loader v-if="isInitialLoading" type="card" class="mt-5" :loading="true" width="300" />
          <v-text-field v-else v-model="search" prepend-icon="mdi-magnify" label="Search anything here ..." single-line
            solo hide-details class="input-search" />
        </v-col>

        <v-col sm="6" md="8" class="d-none d-sm-flex justify-center">
          <v-skeleton-loader v-if="isInitialLoading" type="card" class="mx-auto" :loading="true" width="100%" />
          <template v-else>
            <v-card-title class="title-text py-0">
              <p class="my-auto mr-2">{{ selectedMessage?.subject }}</p>
              <span class="ml-sm-0">{{ formatMySQLDate(selectedMessage?.created_at) }}</span>
            </v-card-title>
            <v-card-title class="title-text py-0" v-if="$canRoute('support') === 1">
              <span v-if="selectedMessage.status == 1" class="close-ticket-inner"
                @click="ticketToggle(selectedMessage)">Close Ticket</span>
              <span v-else class="close-ticket-inner-2" @click="ticketToggle(selectedMessage)">Re-Open</span>
            </v-card-title>
          </template>
        </v-col>
      </v-row>

      <!-- 📬 Ticket Tabs -->
      <v-tabs class="support-tabs" vertical :background-color="$vuetify.theme.dark ? '#131c29' : '#E0EAF2'"
        v-model="selectedTab" v-if="!isInitialLoading">
        <v-tab v-for="(message, index) in visibleMessages" :key="message?.id"
          class="text-left justify-start support-tab" @click="selectMessage(index)">
          <p class="mb-0" :style="selectedTab === index ? 'color: #039de9;' : ''">
            {{ message.subject }}
            <span class="d-block">{{ message?.fname }} - {{ formatMySQLDate(message?.created_at) }}</span>
            <span class="ticket-circle" v-if="message.is_seen != 1">
              <v-icon size="80" color="#039de9">mdi-circle-small</v-icon>
            </span>
          </p>
        </v-tab>

        <!-- Pagination -->
        <v-row dense justify="center" class="my-4">
          <v-pagination v-model="currentPage" :length="totalPages" :total-visible="7" color="primary"
            @input="loadMessages" />
        </v-row>

        <!-- 💬 Ticket Messages -->
        <v-tab-item v-for="(message, index) in visibleMessages" :key="message.id" :value="index" class="bg-transparent">
          <v-card ref="messageCard" class="support-card messages-container" flat
            :color="$vuetify.theme.dark ? '#131c29' : '#E0EAF2'">

            <!-- Ticket Info -->
            <v-row dense>
              <v-col cols="2" sm="2" md="1">
                <v-avatar size="31" class="mx-auto">
                  <img :src="getProfilePicUrl(message?.profile_pic_url)" alt="User Avatar" />
                </v-avatar>
              </v-col>
              <v-col sm="10" cols="10" md="10">
                <v-card flat :color="$vuetify.theme.dark ? '#131c29' : '#E0EAF2'">
                  <v-card-title class="align-baseline">
                    <div class="title-text">
                      <p>
                        <span class="client-name" @click="clientDetails(message?.client_account_id)">
                          {{ message?.fname }} {{ message?.lname }} ({{ message?.client_account_id }})
                        </span>
                        <span> {{ message?.email }} </span>
                      </p>
                    </div>
                    <span class="ml-4 duration">{{ message?.created_at }}</span>
                  </v-card-title>
                  <v-card-text>
                    <p class="bg-theme">{{ message?.description }}</p>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- ✅ Comments -->
            <div v-if="message.ticketcomments && message.ticketcomments.length">
              <v-row dense v-for="comment in message.ticketcomments" :key="comment?.id"
                :justify="comment?.is_admin_comment ? 'end' : 'start'">
                <v-col sm="11" md="10">
                  <v-card class="mb-10" flat :color="$vuetify.theme.dark ? '#131c29' : '#E0EAF2'">
                    <v-card-text
                      :class="comment?.is_admin_comment ? 'justify-end custom-title' : 'justify-start custom-title'">
                      <div class="ma-0 pa-3 rounded-xl shadow-sm"
                        :class="comment?.is_admin_comment ? 'bg-success text-white' : 'light-green-bg'"
                        style="width: 95%; word-break: break-word;">
                        <div class="d-flex justify-space-between align-center">
                          <span>
                            {{ comment?.created_at }}
                            <span class="ml-4">
                              {{ comment?.is_admin_comment
                                ? `Admin - (${comment?.admin?.admin_name}*)`
                                : `Client - (${message?.fname} ${message?.lname})` }}
                            </span>
                          </span>

                          <!-- Admin Menu -->
                          <v-menu v-if="comment?.is_admin_comment && $canRoute('support') === 1" offset-y left>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn icon v-bind="attrs" v-on="on">
                                <v-icon>mdi-dots-vertical</v-icon>
                              </v-btn>
                            </template>
                            <v-list>
                              <v-list-item @click="editComment(comment)">
                                <v-list-item-icon>
                                  <v-icon size="16">mdi-pencil</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>Edit</v-list-item-title>
                              </v-list-item>
                              <v-list-item @click="deleteComment(comment.id)">
                                <v-list-item-icon>
                                  <v-icon size="16">mdi-delete</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>Delete</v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </div>

                        <!-- Comment text -->
                        <div class="mt-2">{{ comment?.comment }}</div>

                        <!-- Attached image -->
                        <v-img v-if="comment.image" :src="comment.image" max-width="150"
                          class="mt-2 rounded-xl shadow" />
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Pending Reply -->
            <v-row v-if="isPendingReply && selectedTab === index" dense justify="end">
              <v-col sm="11" md="10">
                <v-card flat :color="$vuetify.theme.dark ? '#131c29' : '#E0EAF2'">
                  <v-card-title class="justify-end custom-title">
                    <p>{{ getCurrentTime() }} <span class="ml-4">Admin</span></p>
                  </v-card-title>
                  <v-card-text>
                    <p class="bg-success">
                      <v-skeleton-loader type="text" :loading="true" class="mb-0" />
                    </p>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <div ref="scrollAnchor" style="height: 1px;"></div>

            <!-- Edit Dialog (single dialog for whole component) -->
            <v-dialog v-model="editDialog" transition="scale-transition" origin="center center" max-width="500px">
              <v-card :color="$vuetify.theme.dark ? '#131c29' : '#E0EAF2'" class="p-5">
                <v-card-title class="mb-3">Edit Your Reply</v-card-title>
                <v-card-text class="custom">
                  <v-textarea label="Current Reply" v-model="editCommentData.comment" auto-grow rows="4" outlined
                    required />
                </v-card-text>

                <v-card-actions>
                  <v-row dense>
                    <v-col cols="12" class="ml-2 pb-5">
                      <v-btn tile :loading="loading" :disabled="loading" @click="updateComment" elevation="0"
                        class="text-capitalize btn-account mr-3 px-sm-8" color="greenMain">Update</v-btn>
                      <v-btn tile color="#FF567F" @click="closeEditDialog" elevation="0"
                        class="text-capitalize btn-account px-sm-8">Cancel</v-btn>
                    </v-col>
                  </v-row>
                </v-card-actions>
              </v-card>
            </v-dialog>

          </v-card>

          <!-- 📝 Reply Input -->
          <v-sheet v-if="selectedMessage.status == 1 && $canRoute('support') === 1" class="input-field"
            :color="$vuetify.theme.dark ? '#131c29' : '#E0EAF2'">
            <input type="text" placeholder="Reply Here" v-model="comment"
              @keypress.enter="handleSendReply(selectedMessage)" :disabled="isPendingReply" :style="{
                color: $vuetify.theme.dark ? 'white' : 'black',
                backgroundColor: 'transparent',
              }" />
            <v-file-input accept="image/*" dense hide-details prepend-icon="mdi-image" show-size label="Attach Image"
              @change="handleImageUpload" />
            <v-img v-if="selectedImage" :src="imagePreview" max-width="150" class="mt-2 ml-2 rounded-xl shadow" />
            <v-btn small color="greenMain" @click="handleSendReply(selectedMessage)" :loading="isPendingReply"
              :disabled="isPendingReply || (!comment?.trim() && !selectedImage)">
              Send
            </v-btn>
          </v-sheet>
        </v-tab-item>
      </v-tabs>
    </v-container>
  </div>
</template>

<script>
import { formatMySQLDate } from "@/_helpers/formateDate";
import toast from "@/services/Notification";
import { TICKETS__ACTIONS } from "@/store/action-types";
import moment from "moment";
import { mapActions, mapState } from "vuex";

export default {
  data() {
    return {
      editDialog: false,
      editCommentData: { id: null, comment: "" }, // <-- use 'comment' key to match backend
      loading: false, // for update button
      search: "",
      selectedTab: 0,
      comment: null,
      isPendingReply: false,
      isInitialLoading: true,
      selectedImage: null,
      imagePreview: null,
      currentPage: 1,
      totalPages: 1,
      limit: 8,
    };
  },

  computed: {
    ...mapState("tickets", ["messages"]),
    ...mapState("admins", ["loggedAdmin"]),
    
    selectedMessage() {
      return this.visibleMessages[this.selectedTab] || {};
    },

    visibleMessages() {
      if (this.search.trim()) {
        return this.messages
          .filter(
            (m) =>
              m.subject.toLowerCase().includes(this.search.toLowerCase()) ||
              m.description.toLowerCase().includes(this.search.toLowerCase())
          )
          .slice(0, this.limit);
      }
      return this.messages.slice(0, this.limit);
    },
  },
  
  watch: {
    "selectedMessage.ticketcomments"(newVal, oldVal) {
      if (newVal && newVal.length > (oldVal?.length || 0)) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    currentPage() {
      this.loadMessages();
    },
  },

  methods: {
    moment,
    formatMySQLDate,
    ...mapActions("tickets", [
      TICKETS__ACTIONS.MESSAGES,
      TICKETS__ACTIONS.REPLY,
      TICKETS__ACTIONS.TOGGLE_TICKET,
      TICKETS__ACTIONS.DELETECOMMENT,
      TICKETS__ACTIONS.UPDATECOMMENT,
      TICKETS__ACTIONS.MARKSEEN,
    ]),

    selectMessage(index) {
      this.selectedTab = index;
      const message = this.visibleMessages[index];
      console.log(message);
      if (message.status == 1 && message.is_seen != true) {
        this[TICKETS__ACTIONS.MARKSEEN]({
          ticket_id: message.id,
        }).then(() => {
          message.is_seen = true;
        });
      }
    },

    editComment(comment) {
      // Populate dialog model and open
      this.editCommentData.id = comment.id;
      this.editCommentData.comment = comment.comment || "";
      this.editDialog = true;
    },

    async updateComment() {
      if (!this.editCommentData.id) return;
      this.loading = true;
      try {
        const payload = {
          id: this.editCommentData.id,
          comment: this.editCommentData.comment,
        };
        await this[TICKETS__ACTIONS.UPDATECOMMENT](payload);
        toast.success("Comment updated successfully");
        this.editDialog = false;
        // reset
        this.editCommentData = { id: null, comment: "" };
        // reload messages and scroll
        await this.loadMessages();
        this.$nextTick(() => this.scrollToBottom());
      } catch (err) {
        console.error("Update error:", err);
        toast.error("Failed to update comment");
      } finally {
        this.loading = false;
      }
    },

    closeEditDialog() {
      this.editDialog = false;
      this.editCommentData = { id: null, comment: "" };
    },

    async deleteComment(id) {
      try {
        await this[TICKETS__ACTIONS.DELETECOMMENT]({ id });
        this.loadMessages();
        toast.success("Comment deleted");
      } catch (e) {
        console.error(e);
        toast.error("Failed to delete comment");
      }
    },

    async handleSendReply(message) {
      if ((!this.comment?.trim() && !this.selectedImage) || this.isPendingReply)
        return;
      const payload = {
        client_ticket_id: message.id,
        client_id: message.client_account_id,
        comment: this.comment?.trim(),
        admin_id: this.loggedAdmin?.id,
        image: this.selectedImage,
      };
      this.isPendingReply = true;
      try {
        await this.REPLY(payload);
        toast.success("Reply sent successfully");
        this.comment = "";
        this.selectedImage = null;
        this.imagePreview = null;
        this.loadMessages();
      } catch {
        toast.error("Failed to send reply");
      } finally {
        this.isPendingReply = false;
      }
    },

    getProfilePicUrl(url) {
      return url || require("@/assets/avatar.png");
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const c = this.$el.querySelector(".messages-container");
        if (c) c.scrollTop = c.scrollHeight;
      });
    },
    
    async loadMessages() {
      this.isInitialLoading = true;
      try {
        const params = { page: this.currentPage, limit: this.limit, search: this.search };
        const res = await this[TICKETS__ACTIONS.MESSAGES](params);
        if (res?.meta?.lastPage) this.totalPages = res.meta.lastPage;
        this.messages = res.data.map(msg => ({
          ...msg,
          is_seen: Number(msg.is_seen),
        }));
      } catch (error) {
        console.error("Error loading messages:", error);
      } finally {
        this.isInitialLoading = false;
      }
    },

    clientDetails(id) {
      this.$router.push(`/registered-user-details/${id}`);
    },

    ticketToggle(message) {
      this[TICKETS__ACTIONS.TOGGLE_TICKET]({
        client_id: message.client_account_id,
        ticket_id: message.id,
        status: message.status === 1 ? 2 : 1,
      })
        .then(() => {
          toast.success("Ticket status updated successfully");
          this.loadMessages();
        })
        .catch(() => toast.error("Failed to update ticket status"));
    },

    handleImageUpload(event) {
      const file = event?.target?.files ? event.target.files[0] : event;
      if (!file) return;
      this.selectedImage = file;
      this.imagePreview = URL.createObjectURL(file);
    },

    getCurrentTime() {
      return moment().format("YYYY-MM-DD HH:mm:ss");
    },
  },
  mounted() {
    this.loadMessages();
  },
 
};
</script>

<style scoped>
.close-ticket-inner {
  min-width: 110px;
  cursor: pointer;
}

.client-name {
  cursor: pointer;
}

.bg-light-blue {
  background-color: #d8ecff !important;
}

.custom .primary--text {
  color: rgba(3, 157, 233, 0.534) !important;
}

.light-green-bg {
  background-color: #70b67cc7;
  color: rgb(2, 75, 18);
}
</style>
